import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  getDevClubOverride,
  getMe,
  setDevClubOverride,
} from "@/lib/api";
import {
  applyAuthExpiredBoundary,
  removeAllScopedFromCache,
  unscopedKey,
} from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

/** TanStack Query wrapper around `GET /api/me`.
 *
 * Side effects (in `useEffect` so React Strict Mode dev double-mount
 * stays idempotent):
 *   - On success: copy user + profile into the auth store, and
 *     active_scope + selection_required into the scope store. These
 *     stores are the synchronous read sources for non-React code
 *     (the api fetch wrapper, query-key builders).
 *   - On 401: apply the shared auth-expired boundary: clear the
 *     persisted token, mark auth store anonymous, clear scope, and
 *     REMOVE every authenticated/private query from the cache (not
 *     invalidate). `removeQueries` evicts entries entirely so the next
 *     subscriber goes into `pending` and has to wait for a fresh fetch
 *     — vs `invalidateQueries`, which marks data stale but keeps it in
 *     the cache, allowing a synchronous read of the previous user's data
 *     across the auth boundary. Privacy isolation needs removal;
 *     freshness needs invalidation. See `removeAuthenticatedFromCache`
 *     in `lib/queryClient.ts` for the cache primitive.
 *   - On 404 with a DEV club override set: self-heal a stale override
 *     (the club was renamed / the DB reseeded, so the host club no
 *     longer resolves and the middleware 404s every request). Clear the
 *     override and reload once. DEV-only and loop-proof — see the inline
 *     comment on the branch. No-op in production builds.
 *   - On any other error: leave the stores alone. The error bubbles
 *     to the caller via `query.error`.
 *
 * Don't read user/profile from this hook in components — read from
 * `useAuthStore(...)` and `useScopeStore(...)` so a component
 * doesn't need to know whether the cache is warm.
 */
export function useMe() {
  const applyMe = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);

  const query = useQuery({
    queryKey: unscopedKey("me"),
    queryFn: getMe,
    retry: false, // /me's 401 is informational, not a network error
    staleTime: 0,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (query.data) {
      // Multi-club defense-in-depth: if the host-resolved club changed
      // since the last /api/me (a dev override flip, a reassigned
      // subdomain, or any in-SPA club change), evict scoped caches BEFORE
      // applying so club B can never read club A's club-leading entries.
      // A full subdomain navigation reloads the document (empty cache), so
      // this only matters for same-document club changes.
      const prevClubId = useAuthStore.getState().currentClub?.id ?? null;
      const nextClubId = query.data.current_club?.id ?? null;
      if (prevClubId && prevClubId !== nextClubId) {
        removeAllScopedFromCache();
      }
      applyMe(query.data);
      applyScope(query.data);
    }
    if (query.error instanceof ApiError && query.error.status === 401) {
      applyAuthExpiredBoundary();
    }
    // DEV-only self-heal: a stale dev club override (the club's slug or
    // subdomain was renamed / the DB was reseeded) makes the club middleware
    // 404 EVERY /api/* call, wedging the app until localStorage is cleared
    // by hand. Drop the override and reload once — the next load runs
    // without it, so this cannot loop. No-op in production builds
    // (getDevClubOverride is hard-false there).
    if (
      query.error instanceof ApiError &&
      query.error.status === 404 &&
      getDevClubOverride()
    ) {
      setDevClubOverride(null);
      window.location.reload();
    }
  }, [query.data, query.error, applyMe, applyScope]);

  return query;
}
